/* eslint-disable react/prop-types */
import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useNavigate} from 'react-router-dom';
import {FELICITY_FAB_CLEARANCE} from '../../../../constants/felicity';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    RadioGroup,
    Radio,
    FormControlLabel,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    StepEmployeeEmployeeAssociation,
    StepEmployeeStudentAssociation,
    StepExternalRecipients,
    StepFormEditor,
    StepRecipientAssociation,
    StepTemplateSelection,
    StepTriggerConditions,
} from './CreateFormWizardSteps';
import FormPostCreateSetup from '../FormPostCreateSetup';
import FormExpirationDatePicker from '../FormExpirationDatePicker';
import CircleIcon from '@mui/icons-material/Circle';
import CancelIcon from '@mui/icons-material/Close';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import {useGetFormCategoriesQuery} from '../../../../api/services/formCategoriesService';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {
    useCreateExternalUserFormAssignmentsMutation,
    useCreateFormEmployeeAssignmentMutation,
    useCreateFormQuestionAssignmentMutation,
    useCreateFormStudentAssignmentMutation,
} from '../../../../api/services/formAssignmentsService';
import {
    useCreateFormMutation,
    useGetFormTemplatesQuery,
    useUpdateFormMutation,
    useLazyGetFormAccessQuery,
    useManageFormAccessMutation,
} from '../../../../api/services/formsService';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import FORM_TYPE, {
    FORM_TYPE_OPTIONS,
    SETTINGS_FORM_TYPE_OPTIONS,
    getFormBehaviorFromType,
} from '../../../../constants/FormType';
import {
    PARENT_AUDIENCE_TYPE,
    getParentAudienceTypeForSelection,
    resolveBackendAudiencePayload,
} from '../../../../constants/ParentAudienceType';
import useNotification from '../../../../utility/hooks/useNotification';
import useUser from '../../../../utility/hooks/useUser';
import {parseRemindIntervalInput, validateFieldLength, validateGroupFilters} from '../../../../utility/validationUtil';
import EmailsInput from '../../../../shared-components/EmailsInput';
import SettingsSectionBase from '../PrivateFormSettings/SettingsSection';
import FormAccessSettings from '../FormAccessSettings';
import {useCreateApprovalStageMutation} from '../../../../api/services/formApprovalsService';
import {
    useSaveAutoresponderRuleMutation,
    useSetAutoresponderEnabledMutation
} from '../../../../api/services/formAutoresponderService';
import {ROLES} from '../../../../constants/roles';
import ApprovalSettings from '../PrivateFormSettings/ApprovalSettings';
import AutoresponderSettings from '../PrivateFormSettings/AutoresponderSettings';
import RestrictionQuestions from '../PrivateFormSettings/RestrictionQuestions';
import {toTestId} from '../../../../utility/testIdUtil';
import moment from 'moment';

const FORM_NAME_MAX_LENGTH = 128;

const FOOTER_ACTION_BUTTON_WIDTH = 165;
const FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE = {xs: 175, sm: FOOTER_ACTION_BUTTON_WIDTH};
const SECTION_SX = {
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    backgroundColor: '#ffffff',
    boxShadow: '0px 2px 8px rgba(15, 23, 42, 0.05)',
};

const LINKED_EMPLOYEE_AUDIENCE = `${FORM_AUDIENCE_TYPE.employees}|linked`;
const LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE = `${FORM_AUDIENCE_TYPE.employees}|linked-employee`;
const PARENT_WITH_ACCOUNT_AUDIENCE = `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|with-account`;
const PARENT_WITHOUT_ACCOUNT_AUDIENCE = `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|without-account`;
const PARENT_APPLICANT_STATUS_AUDIENCE = `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|applicant-status`;
const LINKED_EMPLOYEE_AUDIENCE_OPTIONS = new Set([
    LINKED_EMPLOYEE_AUDIENCE,
    LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE,
]);

// AD-2689: the order Maria chose, with Email communication under Application or Acceptance form.
const CREATE_FORM_TYPE_ORDER = [
    FORM_TYPE.email,
    FORM_TYPE.selfService,
    FORM_TYPE.triggerForm,
    FORM_TYPE.application,
    FORM_TYPE.emailCommunication,
    FORM_TYPE.shareable,
];

// AD-2689: what each choice means, shown when the user holds the mouse over the 'i'. Checked against the help centre and the code.
const CHOICE_TIPS = {
    [FORM_TYPE.email]: 'Regular forms are emailed to the recipients you add. Reminders are sent to anyone who has not completed the form, if you set a reminder interval.',
    [FORM_TYPE.selfService]: 'Self-service forms are not emailed. Parents find them on the Self-service Forms tab of their child\'s profile, and staff find them in My Forms. They can be completed as often as needed. Parents need an Admicity account to use them.',
    [FORM_TYPE.triggerForm]: 'Choose this form type to send a form linked to a specific question response on another form. It is sent automatically when that response is given.',
    [FORM_TYPE.application]: 'These are public forms that create a new applicant profile on submission.',
    [FORM_TYPE.emailCommunication]: 'Email communications are emailed to the recipients you add, to tell them something. Recipients do not have any questions to answer.',
    [FORM_TYPE.shareable]: 'Public forms can be completed by anyone who has the link, for example on your school website. The responses are not linked to a student or employee profile.',
    parent: 'This form is sent to contacts with priority 1 or 2, who have Parental Responsibility and no restrictive Legal Order.',
    employee: 'This form is sent to employees if you have activated employee accounts in the General tab of School Settings.',
    external: 'This form is sent to people outside the school at the email addresses you enter. A trigger form can also go to an email address given in an answer on another form.',
    [`${FORM_AUDIENCE_TYPE.students}`]: 'Student forms can be used by secondary schools if they want the student to respond directly. Student accounts must be enabled in the General tab of School Settings if you select this option.',
    [`${FORM_AUDIENCE_TYPE.previousSchool}`]: "This form is sent automatically to the email address of the child's current or previous school, as entered on the child's profile. The address is often filled in from the answers on an application or acceptance form.",
    [PARENT_WITH_ACCOUNT_AUDIENCE]: 'Parents are emailed a link to the form. They can also complete it from their Admicity account, and they can see their answers there afterwards. The parents of applicants can only have an account once the place is Accepted.',
    [PARENT_WITHOUT_ACCOUNT_AUDIENCE]: 'Every parent is emailed a link to the form, whether or not they have created an Admicity account.',
    [PARENT_APPLICANT_STATUS_AUDIENCE]: 'For the parents of applicants who have Applied or been Offered a place. They are emailed a link to complete the form, but they cannot see it in an Admicity account afterwards.',
    [`${FORM_AUDIENCE_TYPE.enquiries}`]: 'This form is sent to the contacts of prospective students in your SIMS Enquiries. It is only available to schools that use the SIMS Enquiries module.',
    [`${FORM_AUDIENCE_TYPE.employees}`]: 'Employees complete this form about themselves.',
    [LINKED_EMPLOYEE_AUDIENCE]: 'Employees complete this form about the students or applicants you link it to. The completed form can be shown on the child\'s profile, and you can choose whether the parents can see it there.',
    [LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE]: 'Employees complete this form about the members of staff you link it to.',
    [`external|${FORM_ASSIGNMENT_TYPE.students}`]: 'A separate form is sent for each student or applicant you link it to.',
    [`external|${FORM_ASSIGNMENT_TYPE.employees}`]: 'A separate form is sent for each member of staff you link it to. The completed form is available on the linked employee\'s profile.',
    [`external|${FORM_ASSIGNMENT_TYPE.none}`]: 'Each email address is sent the form once. The responses are kept in Form Responses only.',
};

// The same look as the 'i' beside 'Must appear in all selected groups' (Maria, 15 October 2026). The box closes when the
// mouse leaves the 'i', so it never covers the 'i' on the next line.
const CHOICE_TIP_STYLE = {tooltip: {sx: {bgcolor: 'white', color: '#186BBE', fontSize: 14, fontWeight: 400, boxShadow: 'none', border: '1px solid #186BBE', borderRadius: 1, px: 1, py: 0.5}}};
const ChoiceTip = ({tip}) => (
    <Tooltip title={tip} placement="right" disableInteractive componentsProps={CHOICE_TIP_STYLE}>
        <InfoOutlinedIcon
            fontSize="small"
            data-testid="wizard-choice-tip"
            sx={{color: 'text.secondary', ml: 0.75, verticalAlign: 'middle', cursor: 'help'}}
            onClick={(event) => event.preventDefault()}
        />
    </Tooltip>
);

// A label in brackets goes on its own line under the choice.
const RadioChoiceLabel = ({text, helper, tip}) => {
    if (tip) {
        // The 'i' stays on the same line as the name; a part in brackets goes underneath.
        const at = helper ? -1 : String(text).indexOf(' (');
        return (
            <Box component="span" sx={{display: 'block', lineHeight: 1.3}}>
                {at < 0 ? <RadioChoiceLabel text={text} helper={helper}/> : text.slice(0, at)}
                <ChoiceTip tip={tip}/>
                {at >= 0 && (
                    <Box component="span" sx={{display: 'block', fontSize: 13, color: 'text.secondary'}}>{text.slice(at + 1)}</Box>
                )}
            </Box>
        );
    }
    // AD-2689: Maria's explanation under a choice, where she has given one.
    if (helper) {
        return (
            <Box component="span" sx={{display: 'block', lineHeight: 1.3}}>
                {text}
                <Box component="span" data-testid="wizard-choice-helper" sx={{display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25}}>{helper}</Box>
            </Box>
        );
    }
    const at = String(text).indexOf(' (');
    if (at < 0) return text;
    return (
        <Box component="span" sx={{display: 'block', lineHeight: 1.3}}>
            {text.slice(0, at)}
            <Box component="span" sx={{display: 'block', fontSize: 13, color: 'text.secondary'}}>{text.slice(at + 1)}</Box>
        </Box>
    );
};

const CREATE_FORM_TYPE_OPTIONS = [
    // AD-2689: email notifications are turned off under Restrictions, so that form type is not listed here.
    ...SETTINGS_FORM_TYPE_OPTIONS
        .filter(({id}) => id !== FORM_TYPE.noEmail)
        .map(option => option.id === FORM_TYPE.email ? {...option, description: 'Regular form'} : option),
    ...FORM_TYPE_OPTIONS.filter(({id}) =>
        [
            FORM_TYPE.application,
            FORM_TYPE.shareable,
        ].includes(id)
    )
].sort((a, b) => CREATE_FORM_TYPE_ORDER.indexOf(a.id) - CREATE_FORM_TYPE_ORDER.indexOf(b.id));

const getFormTypeValueForWizard = (selectedType) =>
    selectedType === FORM_TYPE.emailCommunication
        ? FORM_TYPE.email
        : selectedType;

const normaliseTemplateText = (value) => String(value ?? '').trim().toLowerCase();

const isBlankTemplate = (template) => normaliseTemplateText(template?.category) === 'blank';

const getRequiredBlankTemplateType = ({
    formType,
    audience,
    parentAccountType
}) => {
    if (formType === FORM_TYPE.shareable) {
        return 'shareable';
    }

    if (formType === FORM_TYPE.application) {
        return 'application';
    }

    if (
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        ['without', 'applicant-status'].includes(parentAccountType)
    ) {
        return 'offer';
    }

    if (audience === FORM_AUDIENCE_TYPE.enquiries) {
        return 'offer';
    }

    if (audience === FORM_AUDIENCE_TYPE.previousSchool) {
        return 'transition';
    }

    return 'regular';
};

const findBlankTemplate = (templates, requiredType) =>
    templates.find(template =>
        isBlankTemplate(template) &&
        normaliseTemplateText(template.type) === requiredType &&
        !template.schoolId
    ) || null;

const ProgressiveSection = ({
    question,
    header,
    headerIcon,
    subHeader,
    children
}) => (
    <Stack spacing={1.5}>
        {question && (
            <Typography
                variant="h6"
                color="text.primary"
                sx={{
                    fontWeight: 600,
                    lineHeight: 1.35,
                }}
            >
                {question}
            </Typography>
        )}
        {/* AD-2689: a page of the dialogue box already has its title, so its main section has no heading of its own. */}
        {header
            ? (
                <Box sx={SECTION_SX}>
                    <SettingsSectionBase
                        header={header}
                        headerIcon={headerIcon}
                        subHeader={subHeader}
                    >
                        {children}
                    </SettingsSectionBase>
                </Box>
            )
            : children}
    </Stack>
);

const CreateFormWizard = ({onClose}) => {
    const {user} = useUser();
    const {
        showSuccessNotification,
        showErrorNotification,
    } = useNotification();
    const [isCreatingForm, setIsCreatingForm] = useState(false);
    const [selectedFormType, setSelectedFormType] = useState('');
    const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
    const [stage, setStage] = useState('setup');
    const [setupStep, setSetupStep] = useState('type');
    const [isBuildChoiceMade, setIsBuildChoiceMade] = useState(false);
    const navigate = useNavigate();
    const [audienceGroup, setAudienceGroup] = useState('');
    const [isTemplateListShown, setIsTemplateListShown] = useState(true);
    const [isAdvanceAfterTemplate, setIsAdvanceAfterTemplate] = useState(false);
    const [createdFormId, setCreatedFormId] = useState(null);
    // AD-2689 (Maria, 14 October 2026): 'Create form' is the last stage. The box keeps the Access, Approvals and
    // Automatic Responses choices and saves them when the form is created.
    const [boxAccess, setBoxAccess] = useState({users: [], roles: [ROLES.SCHOOL_ADMIN], viewOnlyUsers: [], viewOnlyRoles: []});
    const [boxStages, setBoxStages] = useState([]);
    const [boxRules, setBoxRules] = useState([]);
    const [boxAutoresponderOn, setBoxAutoresponderOn] = useState(false);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [getFormAccess] = useLazyGetFormAccessQuery();
    const [manageFormAccess] = useManageFormAccessMutation();
    const [createApprovalStage] = useCreateApprovalStageMutation();
    const [setAutoresponderEnabled] = useSetAutoresponderEnabledMutation();
    const [saveAutoresponderRule] = useSaveAutoresponderRuleMutation();
    const [schemaValidation, setSchemaValidation] = useState({});
    const [componentToEditIndex, setComponentToEditIndex] = useState(undefined);
    const [isBuilderAtBottom, setIsBuilderAtBottom] = useState(false);
    const [isTemplateSelectionLocked, setIsTemplateSelectionLocked] = useState(false);
    // Each page of the Create Form box opens at the top, not where the page before was scrolled to.
    const setupContentRef = useRef(null);
    useEffect(() => {
        if (setupContentRef.current) setupContentRef.current.scrollTop = 0;
    }, [setupStep]);
    const builderScrollContainerRef = useRef(null);
    const builderBottomSentinelRef = useRef(null);
    const isSmallScreen = useMediaQuery('(max-width:1100px)');

    const schoolLogoSchemaComponent = useMemo(
        () => ({
            type: 'schoolLogo',
            url: user?.schoolLogoUrl
        }),
        [user?.schoolLogoUrl]
    );

    const initialState = useMemo(() => ({
        form: {
            type: null,
            audience: null,
            selection: '',
        },
        template: {
            creationMethod: 'blank',
            templateId: null,
            templateObject: null,
            categoryFilter: null,
            typeFilter: null,
        },
        details: {
            formName: '',
            expiryDate: '',
            goLiveDate: '',
            reminderFrequency: 0,
            allParentsCanSubmit: false,
            sendToPriority1ContactsOnly: false,
            visibleToParents: true,
            shareResponseWithOtherParents: true,
            notificationEmail: [],
            categories: [],
            accessLevel: null,
            parentAccountType: null,
            strictRecipientsFiltering: false,
            allowMultipleSubmissions: false,
            enableRecipientNotifications: false,
            enableAutoresponder: false,
            isCommunication: false,
            profileMustBeComplete: false,
            linkToStudents: false,
            linkToEmployees: false,
            linkedStrictFiltering: false,
            allLinkedEmployeesCanSubmit: false,
        },
        recipients: {
            assignmentType: null,
            student: {
                students: [],
                applications: [],
                enquiries: [],
            },
            employees: [],
            groups: [],
            externalEmails: [],
        },
        associations: {
            students: [],
            applications: [],
            enquiries: [],
            employees: [],
            groups: []
        },
        trigger: {
            selectedForm: null,
            selectedFormSchema: null,
            relatedComponent: null,
            targetState: null,
            relatedFormEmailFieldId: null,
            relatedFormEmployeeFieldId: null,
            externalEmails: [],
        },
        schema: []
    }), []);

    const [wizard, setWizard] = useState(initialState);

    const [createForm] = useCreateFormMutation();
    const [updateForm] = useUpdateFormMutation();
    const [createFormQuestionAssignment] = useCreateFormQuestionAssignmentMutation();
    const [createFormStudentAssignments] = useCreateFormStudentAssignmentMutation();
    const [createFormEmployeeAssignment] = useCreateFormEmployeeAssignmentMutation();
    const [createExternalUserFormAssignments] = useCreateExternalUserFormAssignmentsMutation();
    const {data: allTemplates = []} = useGetFormTemplatesQuery();
    const {data: formCategories = []} = useGetFormCategoriesQuery();
    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: false});

    const computeAccessLevel = (state) => {
        const templateType = state.template.templateObject?.type;
        const isParent = state.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;

        if (
            state.form.audience === FORM_AUDIENCE_TYPE.externalUsers ||
            state.form.audience === FORM_AUDIENCE_TYPE.enquiries
        ) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (isParent && !['with', 'with-account'].includes(state.details.parentAccountType)) {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (templateType === 'transition' || templateType === 'offer') {
            return FORM_ACCESS_LEVELS.publicWithStudentLink;
        }

        if (['acceptance', 'application', 'shareable'].includes(templateType)) {
            return FORM_ACCESS_LEVELS.public;
        }

        return FORM_ACCESS_LEVELS.private;
    };

    const accessLevel = useMemo(
        () => computeAccessLevel(wizard),
        [
            wizard.details.parentAccountType,
            wizard.form.audience,
            wizard.template.templateObject?.type
        ]
    );

    useEffect(() => {
        setWizard(prev => {
            if (prev.details.accessLevel === accessLevel) {
                return prev;
            }

            return {
                ...prev,
                details: {
                    ...prev.details,
                    accessLevel
                }
            };
        });
    }, [accessLevel]);

    useEffect(() => {
        if (!wizard.template.templateObject) {
            return;
        }

        setWizard(prev => ({
            ...prev,
            schema: [
                ...(wizard.template.templateObject.schema ?? []),
                schoolLogoSchemaComponent
            ]
        }));
    }, [
        wizard.template.templateId,
        wizard.template.templateObject,
        schoolLogoSchemaComponent
    ]);

    const requiresAudience = ![FORM_TYPE.application, FORM_TYPE.shareable].includes(wizard.form.type);
    const hasAudienceSelection = !requiresAudience || wizard.form.audience != null;

    const requiredBlankTemplateType = useMemo(
        () => getRequiredBlankTemplateType({
            formType: wizard.form.type,
            audience: wizard.form.audience,
            parentAccountType: wizard.details.parentAccountType,
        }),
        [
            wizard.details.parentAccountType,
            wizard.form.audience,
            wizard.form.type
        ]
    );

    const blankTemplate = useMemo(
        () => (
            hasAudienceSelection
                ? findBlankTemplate(allTemplates, requiredBlankTemplateType)
                : null
        ),
        [
            allTemplates,
            hasAudienceSelection,
            requiredBlankTemplateType
        ]
    );

    const isBlankMethodAvailable = !!blankTemplate;

    useEffect(() => {
        if (wizard.template.creationMethod !== 'blank') {
            return;
        }

        if (!blankTemplate) {
            return;
        }

        if (wizard.template.templateId === blankTemplate.id) {
            return;
        }

        setWizard(prev => ({
            ...prev,
            template: {
                ...prev.template,
                templateId: blankTemplate.id,
                templateObject: blankTemplate,
                categoryFilter: blankTemplate.category,
                typeFilter: blankTemplate.type,
            },
            schema: [...(blankTemplate.schema ?? []), schoolLogoSchemaComponent]
        }));
    }, [
        blankTemplate,
        schoolLogoSchemaComponent,
        wizard.template.creationMethod,
        wizard.template.templateId
    ]);

    useEffect(() => {
        if (!allTemplates.length) {
            return;
        }

        if (wizard.template.creationMethod !== 'blank') {
            return;
        }

        if (!hasAudienceSelection || isBlankMethodAvailable) {
            return;
        }

        setWizard(prev => ({
            ...prev,
            template: {
                ...initialState.template,
                creationMethod: 'template',
            },
            schema: [],
        }));
    }, [
        allTemplates,
        hasAudienceSelection,
        initialState,
        isBlankMethodAvailable,
        wizard.template.creationMethod
    ]);

    const {data: visibleGroupFilters} = useGetGroupFiltersQuery({});
    const isEnquiryFilterVisible = !!visibleGroupFilters?.modules?.some(module => module.name === 'ENQUIRY');

    const audienceOptions = useMemo(() => {
        const baseOptions = [
            {
                value: PARENT_WITH_ACCOUNT_AUDIENCE,
                label: 'Parent with an Admicity account'
            },
            {
                value: PARENT_WITHOUT_ACCOUNT_AUDIENCE,
                label: 'Parent without an Admicity account'
            },
            {
                value: PARENT_APPLICANT_STATUS_AUDIENCE,
                label: 'Parent of applicant with Applied or Offered status'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.previousSchool}`,
                label: 'Child\'s current/previous school'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.employees}`,
                label: 'Employee'
            },
            {
                value: LINKED_EMPLOYEE_AUDIENCE,
                label: 'Employee linked to student'
            },
            {
                value: LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE,
                label: 'Employee linked to employee'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.externalUsers}`,
                label: 'External recipient'
            },
            {
                value: `${FORM_AUDIENCE_TYPE.students}`,
                label: 'Student'
            },
            // AD-2689: Enquiry is offered only where the school uses SIMS Enquiries and its Enquiry filters are visible.
            ...(schoolInfo && schoolInfo.disableEnquiriesSync === false && isEnquiryFilterVisible
                ? [{
                    value: `${FORM_AUDIENCE_TYPE.enquiries}`,
                    label: 'Enquiry'
                }]
                : [])
        ];

        if (selectedFormType === FORM_TYPE.selfService) {
            return baseOptions.filter((option) => {
                const [audienceId] = String(option.value).split('|');
                const numericAudience = Number(audienceId);

                return (
                    numericAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    numericAudience === FORM_AUDIENCE_TYPE.employees
                );
            });
        }

        if (selectedFormType === FORM_TYPE.triggerForm) {
            return baseOptions.filter(
                option => option.value !== `${FORM_AUDIENCE_TYPE.students}`
            );
        }

        if (selectedFormType === FORM_TYPE.emailCommunication) {
            return baseOptions.filter(option => !LINKED_EMPLOYEE_AUDIENCE_OPTIONS.has(option.value));
        }

        return baseOptions;
    }, [schoolInfo, selectedFormType, isEnquiryFilterVisible]);

    const handleFormTypeChange = (event) => {
        const nextSelectedType = event.target.value;
        const nextFormType = getFormTypeValueForWizard(nextSelectedType);
        const nextIsCommunication = nextSelectedType === FORM_TYPE.emailCommunication;
        const nextBehaviour = getFormBehaviorFromType(nextFormType);

        setSelectedFormType(nextSelectedType);
        setAudienceGroup('');
        setIsTemplateDialogOpen(false);
        setWizard({
            ...initialState,
            form: {
                ...initialState.form,
                type: nextFormType,
            },
            details: {
                ...initialState.details,
                allowMultipleSubmissions: nextIsCommunication
                    ? false
                    : nextBehaviour.allowMultipleSubmissions,
                enableRecipientNotifications: nextIsCommunication
                    ? true
                    : nextBehaviour.enableRecipientNotifications,
                isCommunication: nextIsCommunication,
            }
        });
    };

    const handleAudienceChange = (event) => {
        const selectedAudienceValue = event.target.value;
        const isLinkedToStudentAudience = selectedAudienceValue === LINKED_EMPLOYEE_AUDIENCE;
        const isLinkedToEmployeeAudience = selectedAudienceValue === LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE;
        const isLinkedAudience = isLinkedToStudentAudience || isLinkedToEmployeeAudience;
        const [audienceId, optionType] = String(selectedAudienceValue).split('|');
        const numericAudience = isLinkedAudience
            ? FORM_AUDIENCE_TYPE.employees
            : Number(audienceId);
        const parentAccountType =
            numericAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                ? (optionType || null)
                : null;

        setIsTemplateDialogOpen(false);
        setWizard(prev => ({
            ...prev,
            form: {
                ...prev.form,
                selection: selectedAudienceValue,
                audience: numericAudience,
            },
            template: {
                ...initialState.template,
            },
            recipients: initialState.recipients,
            trigger: initialState.trigger,
            associations: {
                ...initialState.associations,
            },
            schema: [],
            details: {
                ...prev.details,
                parentAccountType,
                linkToStudents: isLinkedToStudentAudience,
                linkToEmployees: isLinkedToEmployeeAudience,
                allLinkedEmployeesCanSubmit: false,
                linkedStrictFiltering: false,
            }
        }));
    };

    // AD-2689: the audience page lists Parent and Employee once each; the next page asks which parents or employees.
    const PARENT_AUDIENCE_VALUES = [PARENT_WITH_ACCOUNT_AUDIENCE, PARENT_APPLICANT_STATUS_AUDIENCE, PARENT_WITHOUT_ACCOUNT_AUDIENCE, `${FORM_AUDIENCE_TYPE.enquiries}`];
    const EMPLOYEE_AUDIENCE_VALUES = [`${FORM_AUDIENCE_TYPE.employees}`, LINKED_EMPLOYEE_AUDIENCE, LINKED_EMPLOYEE_TO_EMPLOYEE_AUDIENCE];
    const isExternalOffered = audienceOptions.some(option => option.value === `${FORM_AUDIENCE_TYPE.externalUsers}`);
    const audienceDetailOptions = {
        // The explanation of 'Parent without an Admicity account' is in its 'i', like the other two choices.
        parent: PARENT_AUDIENCE_VALUES.map(value => audienceOptions.find(option => option.value === value)).filter(Boolean),
        employee: EMPLOYEE_AUDIENCE_VALUES.map(value => audienceOptions.find(option => option.value === value)).filter(Boolean),
        // External recipients are linked to students/applicants or employees, or to neither.
        external: isExternalOffered
            ? [
                {value: `external|${FORM_ASSIGNMENT_TYPE.students}`, label: 'Linked to students/applicants'},
                {value: `external|${FORM_ASSIGNMENT_TYPE.employees}`, label: 'Linked to employees'},
                {value: `external|${FORM_ASSIGNMENT_TYPE.none}`, label: 'Not linked to a student or employee'},
            ]
            : [],
    };
    const isAudienceGroup = (value) => value === 'parent' || value === 'employee' || value === 'external';
    const audienceGroupOptions = [
        ...(audienceDetailOptions.parent.length ? [{value: 'parent', label: 'Parent'}] : []),
        ...(audienceDetailOptions.employee.length ? [{value: 'employee', label: 'Employee'}] : []),
        ...[`${FORM_AUDIENCE_TYPE.students}`]
            .map(value => audienceOptions.find(option => option.value === value))
            .filter(Boolean),
        ...(isExternalOffered ? [{value: 'external', label: 'External recipient'}] : []),
        ...[`${FORM_AUDIENCE_TYPE.previousSchool}`]
            .map(value => audienceOptions.find(option => option.value === value))
            .filter(Boolean),
    ];
    const audienceDetailValue = audienceGroup === 'external'
        ? (wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers && wizard.recipients?.assignmentType != null
            ? `external|${wizard.recipients.assignmentType}`
            : '')
        : (wizard.form.selection || '');
    const handleAudienceDetailChange = (event) => {
        const value = event.target.value;
        if (!String(value).startsWith('external|')) {
            handleAudienceChange(event);
            return;
        }
        handleAudienceChange({target: {value: `${FORM_AUDIENCE_TYPE.externalUsers}`}});
        setWizard(prev => ({
            ...prev,
            recipients: {...prev.recipients, assignmentType: Number(String(value).split('|')[1])},
        }));
    };
    // A page with one choice (an email to employees offers only 'Employee') has it chosen already
    // (walkthrough finding 30, 15 October 2026).
    useEffect(() => {
        const choices = audienceDetailOptions[audienceGroup];
        if (setupStep === 'audienceDetail' && choices?.length === 1 && !audienceDetailValue) {
            handleAudienceDetailChange({target: {value: choices[0].value}});
        }
    });
    const handleAudienceGroupChange = (event) => {
        const group = event.target.value;
        setAudienceGroup(group);
        if (isAudienceGroup(group)) {
            setWizard(prev => ({...prev, form: {...prev.form, selection: '', audience: null}}));
        } else {
            handleAudienceChange({target: {value: group}});
        }
    };

    const handleBuildMethodChange = (event) => {
        const nextCreationMethod = event.target.value;
        setIsBuildChoiceMade(true);

        setWizard(prev => ({
            ...prev,
            template: {
                ...initialState.template,
                creationMethod: nextCreationMethod,
            },
            schema: [],
            details: {
                ...prev.details,
                formName: '',
                expiryDate: '',
                goLiveDate: '',
                reminderFrequency: 0,
                notificationEmail: [],
                categories: [],
                enableAutoresponder: false,
            }
        }));

        if (nextCreationMethod === 'template' || nextCreationMethod === 'schoolTemplate') {
            // AD-2689: the template list shows in the same box.
            setIsTemplateListShown(true);
        } else {
            setIsTemplateDialogOpen(false);
        }
    };

    const isTypeSelected = !!selectedFormType;
    const isTriggerForm = wizard.form.type === FORM_TYPE.triggerForm;
    const isTriggerConfigured =
        !isTriggerForm ||
        (
            !!wizard.trigger.selectedForm?.id &&
            !!wizard.trigger.relatedComponent &&
            !!wizard.trigger.targetState &&
            // A trigger form for external recipients needs someone to go to: an email question on the other
            // form, or at least one address.
            (
                wizard.form.audience !== FORM_AUDIENCE_TYPE.externalUsers ||
                !!wizard.trigger.relatedFormEmailFieldId ||
                (wizard.trigger.externalEmails ?? []).some(x => !x.isDeleted)
            )
        );
    const showRecipientSection =
        hasAudienceSelection &&
        !isTriggerForm &&
        [
            FORM_AUDIENCE_TYPE.externalUsers,
            FORM_AUDIENCE_TYPE.parentsForChildCompletion,
            FORM_AUDIENCE_TYPE.enquiries,
            FORM_AUDIENCE_TYPE.students,
            FORM_AUDIENCE_TYPE.previousSchool,
            FORM_AUDIENCE_TYPE.employees
        ].includes(wizard.form.audience);
    const showExternalRecipients =
        showRecipientSection &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers;
    const showLinkedStudents =
        showRecipientSection &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
        wizard.details.linkToStudents;
    const showLinkedEmployees =
        showRecipientSection &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
        wizard.details.linkToEmployees;
    // AD-2673: both parents can be asked to complete a form whether or not they have an account, so the
    // question is asked for applicants' parents at Applied or Offered too. Blocking until the profile is
    // complete needs an account, so that question stays for parents with one.
    const showParentCompletion =
        hasAudienceSelection &&
        wizard.details.isCommunication !== true &&
        wizard.form.type !== FORM_TYPE.selfService &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        wizard.details.parentAccountType != null;
    const parentsHaveAccounts = ['with', 'with-account'].includes(wizard.details.parentAccountType);
    // On a self-service form linked to students, this decides who receives the emails sent to the linked
    // student's parents, as in Form Settings.
    const isLinkedStudentSelfService =
        wizard.form.type === FORM_TYPE.selfService &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
        !!wizard.details.linkToStudents;
    const showSendToPriority1Only =
        hasAudienceSelection &&
        (wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion || isLinkedStudentSelfService);
    const showVisibleToParents =
        hasAudienceSelection &&
        wizard.details.isCommunication !== true &&
        wizard.form.type !== FORM_TYPE.selfService &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
        accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink;

    const isRecipientSectionComplete = !showRecipientSection || true;
    const isExternalRecipientsSectionComplete = !showExternalRecipients || true;
    const isLinkedStudentsSectionComplete = !showLinkedStudents || true;
    const isLinkedEmployeesSectionComplete = !showLinkedEmployees || true;
    const isParentCompletionSectionComplete = !showParentCompletion || true;

    const isPreBuildSectionComplete =
        isTypeSelected &&
        hasAudienceSelection &&
        isTriggerConfigured &&
        isRecipientSectionComplete &&
        isExternalRecipientsSectionComplete &&
        isLinkedStudentsSectionComplete &&
        isLinkedEmployeesSectionComplete &&
        isParentCompletionSectionComplete;
    const shouldSkipTemplateSelection = wizard.details.isCommunication === true;

    const isTemplateMode =
        wizard.template.creationMethod === 'template' ||
        wizard.template.creationMethod === 'schoolTemplate';
    const hasAssignedTemplate = !!wizard.template.templateId;
    const isBuildSectionComplete =
        shouldSkipTemplateSelection
            ? hasAssignedTemplate
            : (
                !!wizard.template.creationMethod &&
                hasAssignedTemplate
            );
    const canShowFormDetailsSection =
        isPreBuildSectionComplete &&
        isBuildSectionComplete;
    const formNameValidation = validateFieldLength(wizard.details.formName, FORM_NAME_MAX_LENGTH);

    const canShowSettingsSection =
        canShowFormDetailsSection &&
        formNameValidation.isValid;

    // Walkthrough finding 32 (Maria, 15 October 2026): an email communication has no expiry.
    const shouldShowExpirationDate = wizard.form.type !== FORM_TYPE.selfService && wizard.details.isCommunication !== true;
    const shouldShowReminderField =
        wizard.details.isCommunication !== true &&
        wizard.form.type !== FORM_TYPE.selfService &&
        wizard.form.type !== FORM_TYPE.shareable &&
        wizard.form.type !== FORM_TYPE.application;
    const shouldShowNotificationEmails = wizard.details.isCommunication !== true;

    const selectedTemplateLabel =
        wizard.template.templateObject?.name ||
        wizard.template.templateObject?.formName ||
        '';
    const templateSource =
        wizard.template.creationMethod === 'schoolTemplate'
            ? 'school'
            : 'admicity';

    const isSettingsSectionComplete = () => {
        if (!canShowSettingsSection) {
            return false;
        }

        // AD-2689: an empty reminder box means no reminders (saved as 0).
        const expiry = wizard.details.expiryDate ? moment(wizard.details.expiryDate) : null;
        const goLive = wizard.details.goLiveDate ? moment(wizard.details.goLiveDate) : null;

        return !(expiry && goLive && expiry.isBefore(goLive));
    };

    // AD-2689: the setup is a dialogue box. Form Setup (type, audience, how to create) cannot be changed later;
    // Form Settings has one page per section, in the wizard's order.
    const hasRecipientsPage = isTriggerForm || showRecipientSection;
    const showEmailNotificationRestriction = selectedFormType === FORM_TYPE.email;
    // AD-2689: a self-service form for employees linked to a student or an employee asks the same two questions as
    // Form Settings: whether the responses appear on the linked profile, and whether the child's parents see them.
    const showLinkedProfileQuestions = wizard.form.type === FORM_TYPE.selfService &&
        (!!wizard.details.linkToStudents || !!wizard.details.linkToEmployees);
    // AD-2695: every form with responses can allow them to be edited; an email has none.
    const showResponseEditing = hasAudienceSelection && wizard.details.isCommunication !== true;
    const isAboutAChild = wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
        !!wizard.details.linkToStudents;
    const hasRestrictionsPage = showEmailNotificationRestriction || showParentCompletion ||
        showSendToPriority1Only || showVisibleToParents || showLinkedProfileQuestions || showResponseEditing;
    const hasNotificationsPage = shouldShowReminderField || shouldShowNotificationEmails;
    // AD-2689 (Maria, 14 October 2026): application, acceptance and public forms are shared by their link, so the
    // page where Recipients would be says where to copy it.
    const isSharedByLink = accessLevel === FORM_ACCESS_LEVELS.public;
    const isEmailBox = wizard.details.isCommunication === true;
    const hasApprovalsPage = !isEmailBox && !!accessLevel && !isSharedByLink &&
        ![FORM_TYPE.application, FORM_TYPE.shareable].includes(selectedFormType);
    const createdPages = [
        'access',
        ...(hasApprovalsPage ? ['approvals'] : []),
        ...(isEmailBox ? [] : ['autoresponses']),
    ];
    const setupSteps = [
        'type',
        // Application and public forms are not sent to an audience, so the box does not ask who they are for.
        ...(requiresAudience ? ['audience'] : []),
        ...(requiresAudience && isAudienceGroup(audienceGroup) ? ['audienceDetail'] : []),
        ...(shouldSkipTemplateSelection ? [] : ['build']),
        ...(hasRecipientsPage ? ['recipients'] : []),
        ...(isSharedByLink && !hasRecipientsPage ? ['sharing'] : []),
        ...(hasRestrictionsPage ? ['restrictions'] : []),
        'name',
        ...(shouldShowExpirationDate ? ['expiry'] : []),
        ...(hasNotificationsPage ? ['notifications'] : []),
        ...createdPages,
    ];
    const setupStepNumber = setupSteps.indexOf(setupStep) + 1;
    const isSettingsStep = !['type', 'audience', 'audienceDetail', 'build'].includes(setupStep);
    const isLastSetupStep = setupStepNumber === setupSteps.length;
    // The page before Access is where the form is created.
    // The template list needs a little more room than the other pages (Maria: not much wider).
    const isWideBuildPage = setupStep === 'build' && isTemplateMode && (isTemplateListShown || !selectedTemplateLabel);
    const setupQuestion = {
        type: 'What type of form is this?',
        audience: `Who is this ${isEmailBox ? 'email' : 'form'} for?`,
        audienceDetail: {
            employee: `Which employees is this ${isEmailBox ? 'email' : 'form'} for?`,
            external: 'Is this external recipient linked to a student/applicant or an employee?',
        }[audienceGroup] ?? `Which parents will this ${isEmailBox ? 'email' : 'form'} be sent to?`,
        build: 'How would you like to create this form?',
        // AD-2689: a trigger form has no recipients to add; it is sent when an answer is given on another form.
        recipients: isTriggerForm ? 'Recipients' : 'Add Recipients',
        restrictions: 'Restrictions',
        name: wizard.details.isCommunication ? 'Email Name and Category' : 'Form Name and Category',
        expiry: 'Expiry Date',
        // Application, acceptance and public forms have no reminders, so their page is Notifications only.
        notifications: shouldShowReminderField ? 'Reminders and Notifications' : 'Notifications',
        sharing: 'Public Link',
        access: 'Form Access',
        approvals: 'Approvals',
        autoresponses: 'Automatic Responses',
    }[setupStep];
    const setupLine = {
        type: 'The form type cannot be changed once the form has been created.',
        audience: 'The audience cannot be changed after the form has been created.',
        audienceDetail: audienceGroup === 'employee' || audienceGroup === 'external'
            ? 'The audience cannot be changed after the form has been created.'
            : 'You can edit the parent type in form settings later, if you need to.',
        recipients: isTriggerForm
            ? 'This form will be sent automatically when a particular answer is given on another form. Choose the form, the question and the answer below. You can edit this in Form Settings later if you need to.'
            : `Recipients can be added or changed later, in the Settings tab of your ${isEmailBox ? 'email' : 'form'}.`,
        restrictions: isEmailBox
            ? "Restrictions are the exception, not the rule. If you don't have a specific reason to add a restriction to your email, leave these on their default 'no' setting and press NEXT."
            : "Form Restrictions are the exception, not the rule. If you don't have a specific reason to add a restriction to your form, leave these on their default 'no' setting and press NEXT.",
        name: isEmailBox
            ? 'You can edit your email name and category later in the email settings, if you need to.'
            : 'You can edit your form name and category later in the form settings, if you need to.',
        expiry: 'If you need to stop any further form responses being submitted after a specific date, enter your expiry date here. Expiry dates can be added or edited in the settings tab of your form later. If there is no submission deadline for your form, press NEXT to continue.',
        build: 'These settings cannot be changed after the form has been created. If you choose a template, you can customise it in the form builder.',
        sharing: 'This form is not sent to recipients. After the form has been created, copy its link from the Settings tab of your form, or click on \'Copy URL\' in the three dot menu on your form list. You can then add the link to your school website or to an email.',
        access: 'You and School Admin users have access by default. You can allow access to any other staff who need it. Staff can only use School Groups shared with them. You can change this later in Settings.',
        approvals: 'If the responses to this form must be approved by one or more members of staff, add an approval stage for each of them here. Approvals can be added or changed later in the Settings tab of your form. If your form does not need approving, press NEXT.',
        // Application, acceptance and public forms are not linked to a student when they are submitted.
        autoresponses: accessLevel === FORM_ACCESS_LEVELS.public
            ? 'An automatic response is an email sent when the form is submitted. You can add one here if it should be sent for every submission. If it depends on the answers to the questions on your form, add it later in the Settings tab of your form, after you have built the questions in the form builder.'
            : 'An automatic response is an email sent when the form is submitted. You can add one here if it should be sent for every submission, or if it depends on the student\'s house, form, year group or intake. If it depends on the answers to the questions on your form, add it later in the Settings tab of your form, after you have built the questions in the form builder.',
    }[setupStep] ?? "If you're not sure what settings you need yet, or if you change your mind, you can edit them later in the 'Form Settings' tab of your form.";
    const buildMethodChoices = [
        {value: 'template', label: 'Use an Admicity Template', testId: 'option-use-an-admicity-template'},
        {value: 'schoolTemplate', label: 'Use a School Template', testId: 'option-use-a-school-template'},
        ...((isBlankMethodAvailable || wizard.template.creationMethod === 'blank')
            ? [{value: 'blank', label: 'Start from a blank form', testId: 'option-blank-form'}]
            : []),
    ];
    const recipientGroupsCheck = validateGroupFilters(wizard.recipients?.groups ?? []);
    const canGoNext = {
        type: isTypeSelected,
        audience: !!audienceGroup && (isAudienceGroup(audienceGroup) || hasAudienceSelection),
        audienceDetail: hasAudienceSelection && !!audienceDetailValue,
        build: isBuildSectionComplete,
        // The same rule as Form Settings, so the box never creates a form that cannot then be saved.
        recipients: isTriggerConfigured && recipientGroupsCheck.isValid,
        restrictions: true,
        name: formNameValidation.isValid && !!wizard.details.formName?.trim(),
        expiry: true,
        notifications: true,
        sharing: true,
        access: true,
        approvals: true,
        autoresponses: true,
    }[setupStep] ?? false;
    useEffect(() => {
        if (isAdvanceAfterTemplate && setupStep === 'build' && isBuildSectionComplete) {
            setIsAdvanceAfterTemplate(false);
            goToNextSetupStep();
        }
    });

    const goToNextSetupStep = () => {
        const nextStep = setupSteps[setupStepNumber];
        // AD-2689: 'Use an Admicity Template' is the choice shown first, until the user picks one.
        // Admicity has no templates for public forms, so a public form opens on 'Start from a blank form'
        // (walkthrough finding 33, 15 October 2026).
        if (nextStep === 'build' && !isBuildChoiceMade && wizard.template.creationMethod === 'blank' &&
            wizard.form.type !== FORM_TYPE.shareable) {
            setWizard(prev => ({...prev, template: {...initialState.template, creationMethod: 'template'}, schema: []}));
        }
        setSetupStep(nextStep);
    };
    const setDetail = (key, value) => setWizard(prev => ({...prev, details: {...prev.details, [key]: value}}));
    const restrictions = [
        showEmailNotificationRestriction && {
            key: 'emailNotificationsOff',
            question: 'Turn off email notifications for this form',
            checked: wizard.form.type === FORM_TYPE.noEmail,
            onChange: (on) => setWizard(prev => ({
                ...prev,
                form: {...prev.form, type: on ? FORM_TYPE.noEmail : FORM_TYPE.email},
                details: {...prev.details, enableRecipientNotifications: !on},
            })),
        },
        showParentCompletion && {
            key: 'bothParents',
            question: 'Both parents must complete their own separate copy of this form',
            checked: !!wizard.details.allParentsCanSubmit,
            onChange: (on) => setWizard(prev => ({
                ...prev,
                // Walkthrough finding 13: a separate copy for each parent and Parent 1 only cannot both be on.
                details: {...prev.details, allParentsCanSubmit: on, shareResponseWithOtherParents: false, ...(on ? {sendToPriority1ContactsOnly: false} : {})},
            })),
        },
        showParentCompletion && wizard.details.allParentsCanSubmit && {
            key: 'shareWithOtherParent',
            question: "Allow each parent to see the other parent's completed form?",
            checked: !!wizard.details.shareResponseWithOtherParents,
            onChange: (on) => setDetail('shareResponseWithOtherParents', on),
        },
        showParentCompletion && parentsHaveAccounts && {
            key: 'profileComplete',
            question: "Block form submission if the child's profile information is incomplete",
            checked: !!wizard.details.profileMustBeComplete,
            onChange: (on) => setDetail('profileMustBeComplete', on),
        },
        showSendToPriority1Only && {
            key: 'parent1Only',
            question: isLinkedStudentSelfService
                ? "Send the emails for the linked student's parents to Parent 1 only"
                : `Send this ${isEmailBox ? 'email' : 'form'} to Parent 1 only`,
            checked: !!wizard.details.sendToPriority1ContactsOnly,
            onChange: (on) => setWizard(prev => ({
                ...prev,
                details: {...prev.details, sendToPriority1ContactsOnly: on, ...(on ? {allParentsCanSubmit: false, shareResponseWithOtherParents: false} : {})},
            })),
        },
        showLinkedProfileQuestions && {
            key: 'linkedProfile',
            question: "Show submitted responses on the linked person's profile",
            checked: !!wizard.details.showResponsesInLinkedProfile,
            onChange: (on) => setWizard(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    showResponsesInLinkedProfile: on,
                    showResponsesToLinkedParents: on ? prev.details.showResponsesToLinkedParents : false,
                },
            })),
        },
        showLinkedProfileQuestions && wizard.details.linkToStudents && wizard.details.showResponsesInLinkedProfile && {
            key: 'linkedParents',
            question: "Let the child's parents see the submitted responses",
            checked: !!wizard.details.showResponsesToLinkedParents,
            onChange: (on) => setDetail('showResponsesToLinkedParents', on),
        },
        showVisibleToParents && {
            key: 'availableToParents',
            question: "Make this form available in the parents' account and on the student profile",
            checked: !!wizard.details.visibleToParents,
            onChange: (on) => setDetail('visibleToParents', on),
        },
        // AD-2695: the person who submitted a response, or a School Admin, can change its answers.
        showResponseEditing && {
            key: 'allowResponseEditing',
            question: 'Allow submitted responses to be edited',
            checked: !!wizard.details.allowResponseEditing,
            onChange: (on) => setWizard(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    allowResponseEditing: on,
                    editUpdatesProfile: on ? prev.details.editUpdatesProfile : false,
                    editResendsResponses: on ? prev.details.editResendsResponses : false,
                },
            })),
        },
        showResponseEditing && wizard.details.allowResponseEditing && isAboutAChild && {
            key: 'editUpdatesProfile',
            question: "Update the child's profile with the edited answers",
            checked: !!wizard.details.editUpdatesProfile,
            onChange: (on) => setDetail('editUpdatesProfile', on),
        },
        showResponseEditing && wizard.details.allowResponseEditing && {
            key: 'editResendsResponses',
            question: 'Send the trigger forms and automatic responses again when a response is edited',
            checked: !!wizard.details.editResendsResponses,
            onChange: (on) => setDetail('editResendsResponses', on),
        },
    ].filter(Boolean);

    const isSetupValid = () =>
        isTypeSelected &&
        hasAudienceSelection &&
        isTriggerConfigured &&
        recipientGroupsCheck.isValid &&
        isBuildSectionComplete &&
        formNameValidation.isValid &&
        isSettingsSectionComplete();

    const getResolvedParentAudienceType = () => {
        const mappedParentAudienceType = getParentAudienceTypeForSelection(
            wizard.form.audience,
            wizard.details.parentAccountType
        );

        return (
            wizard.form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            mappedParentAudienceType === PARENT_AUDIENCE_TYPE.none &&
            wizard.template.templateObject?.type === 'offer'
                ? PARENT_AUDIENCE_TYPE.applicant
                : mappedParentAudienceType
        );
    };

    const getAssignmentType = () => {
        if (wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers) {
            if (wizard.recipients.assignmentType) {
                return wizard.recipients.assignmentType;
            }

            if (wizard.recipients.employees.length) {
                return FORM_ASSIGNMENT_TYPE.employees;
            }

            return FORM_ASSIGNMENT_TYPE.students;
        }

        switch (wizard.form.audience) {
            case FORM_AUDIENCE_TYPE.employees:
                return FORM_ASSIGNMENT_TYPE.employees;
            case FORM_AUDIENCE_TYPE.students:
            case FORM_AUDIENCE_TYPE.enquiries:
            case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            case FORM_AUDIENCE_TYPE.previousSchool:
                return FORM_ASSIGNMENT_TYPE.students;
            default:
                return FORM_ASSIGNMENT_TYPE.none;
        }
    };

    const buildFormPayload = () => {
        const isCommunicationForm =
            wizard.details.isCommunication ||
            selectedFormType === FORM_TYPE.emailCommunication;
        const audiencePayload = resolveBackendAudiencePayload(
            wizard.form.audience || 0,
            getResolvedParentAudienceType()
        );

        const schema =
            wizard.schema && wizard.schema.length
                ? wizard.schema
                : [
                    ...(wizard.template.templateObject?.schema ?? []),
                    schoolLogoSchemaComponent
                ];

        return {
            templateId: wizard.template.templateId,
            schema,
            name: wizard.details.formName.trim(),
            remindInterval: wizard.details.reminderFrequency || 0,
            categoryIds: wizard.details.categories,
            strictRecipientsFiltering: wizard.details.strictRecipientsFiltering,
            allParentsCanSubmit: wizard.details.allParentsCanSubmit,
            sendToPriority1ContactsOnly: wizard.details.sendToPriority1ContactsOnly,
            visibleToParents: wizard.details.visibleToParents ?? true,
            showResponsesInLinkedProfile: !!wizard.details.showResponsesInLinkedProfile,
            showResponsesToLinkedParents: !!wizard.details.showResponsesToLinkedParents,
            allowResponseEditing: !!wizard.details.allowResponseEditing,
            editUpdatesProfile: !!wizard.details.allowResponseEditing && !!wizard.details.editUpdatesProfile,
            editResendsResponses: !!wizard.details.allowResponseEditing && !!wizard.details.editResendsResponses,
            shareResponseWithOtherParents: wizard.details.shareResponseWithOtherParents ?? true,
            allowMultipleSubmissions: wizard.details.allowMultipleSubmissions,
            enableRecipientNotifications: wizard.details.enableRecipientNotifications,
            audience: audiencePayload.audience,
            parentAudienceType: audiencePayload.parentAudienceType,
            accessLevel: computeAccessLevel(wizard),
            expirationDate: wizard.details.expiryDate || null,
            notificationEmails: wizard.details.notificationEmail,
            assignmentType: getAssignmentType(),
            scheduledActivationAt: wizard.details.goLiveDate || null,
            enableAutoresponder: wizard.details.enableAutoresponder,
            isCommunication: isCommunicationForm,
            ProfileMustBeComplete: wizard.details.profileMustBeComplete,
            linkToStudents: wizard.details.linkToStudents || false,
            linkToEmployees: wizard.details.linkToEmployees || false,
            allLinkedEmployeesCanSubmit:
                (wizard.details.linkToStudents || wizard.details.linkToEmployees) &&
                !!wizard.details.allLinkedEmployeesCanSubmit,
            linkedStrictFiltering: wizard.details.linkedStrictFiltering || false,
        };
    };

    const saveRecipients = async (formId) => {
        const {
            form: {audience},
            details: {linkToStudents, linkToEmployees},
            recipients: {
                assignmentType,
                student: {
                    students: studentFilters = [],
                    applications: applicationFilters = [],
                    enquiries: enquiryFilters = [],
                } = {},
                employees: employeeFilters = [],
                groups: groupFilters = [],
                externalEmails: externalUserEmailAssignments = [],
            },
            associations: {
                students: linkedStudentFilters = [],
                applications: linkedApplicationFilters = [],
                enquiries: linkedEnquiryFilters = [],
                employees: linkedEmployeeFilters = [],
                groups: linkedGroupFilters = [],
            } = {},
        } = wizard;
        const isTriggerBasedForm = wizard.form.type === FORM_TYPE.triggerForm;

        const tasks = [];

        const allStudentRecipients = [
            ...studentFilters,
            ...applicationFilters,
            ...enquiryFilters,
        ].map(item => ({
            personId: item.id,
            name: item.label,
        }));

        const allEmployeeRecipients = employeeFilters.map(item => ({
            personId: item.id,
            name: item.label || `${item.preferredForename ?? ''} ${item.preferredSurname ?? ''}`.trim(),
        }));

        const createdGroupFilters = groupFilters.map(item => ({
            filterId: item.id,
            excluded: !!item.excluded,
        }));

        const externalRecipientEmailAssignmentsForThisAudience =
            wizard.form.type === FORM_TYPE.triggerForm &&
            audience === FORM_AUDIENCE_TYPE.externalUsers
                ? (wizard.trigger.externalEmails ?? [])
                : externalUserEmailAssignments;

        const externalEmails = externalRecipientEmailAssignmentsForThisAudience
            .filter(item => !item.isDeleted)
            .map(item => item.email);

        const shouldAssignStudents =
            !isTriggerBasedForm &&
            (
                [
                    FORM_AUDIENCE_TYPE.students,
                    FORM_AUDIENCE_TYPE.enquiries,
                    FORM_AUDIENCE_TYPE.parentsForChildCompletion,
                    FORM_AUDIENCE_TYPE.previousSchool,
                ].includes(audience) ||
                (
                    audience === FORM_AUDIENCE_TYPE.externalUsers &&
                    assignmentType === FORM_ASSIGNMENT_TYPE.students
                )
            );

        const shouldAssignEmployees =
            !isTriggerBasedForm &&
            (
                audience === FORM_AUDIENCE_TYPE.employees ||
                (
                    audience === FORM_AUDIENCE_TYPE.externalUsers &&
                    assignmentType === FORM_ASSIGNMENT_TYPE.employees
                )
            );

        if (shouldAssignStudents && (allStudentRecipients.length || createdGroupFilters.length)) {
            tasks.push(
                createFormStudentAssignments({
                    formId,
                    personAssignments: allStudentRecipients,
                    groupFilters: createdGroupFilters,
                }).unwrap()
            );
        }

        if (shouldAssignEmployees && (allEmployeeRecipients.length || createdGroupFilters.length)) {
            tasks.push(
                createFormEmployeeAssignment({
                    formId,
                    personAssignments: allEmployeeRecipients,
                    groupFilters: createdGroupFilters,
                }).unwrap()
            );
        }

        if (audience === FORM_AUDIENCE_TYPE.externalUsers && externalEmails.length) {
            tasks.push(
                createExternalUserFormAssignments({
                    formId,
                    emails: externalEmails,
                }).unwrap()
            );
        }

        if (audience === FORM_AUDIENCE_TYPE.employees && linkToStudents === true) {
            const linkedPersonAssignments = [
                ...linkedStudentFilters,
                ...linkedApplicationFilters,
                ...linkedEnquiryFilters,
            ].map(item => ({
                personId: item.id,
                name: item.label,
            }));

            const linkedGroupFiltersPayload = linkedGroupFilters.map(item => ({
                filterId: item.id,
                excluded: !!item.excluded,
            }));

            if (linkedPersonAssignments.length || linkedGroupFiltersPayload.length) {
                tasks.push(
                    createFormStudentAssignments({
                        formId,
                        personAssignments: linkedPersonAssignments,
                        groupFilters: linkedGroupFiltersPayload,
                        linkedStudent: true,
                    }).unwrap()
                );
            }
        }

        if (audience === FORM_AUDIENCE_TYPE.employees && linkToEmployees === true) {
            const linkedPersonAssignments = linkedEmployeeFilters.map(item => ({
                personId: item.id,
                name: item.label || `${item.preferredForename ?? ''} ${item.preferredSurname ?? ''}`.trim(),
            }));

            const linkedGroupFiltersPayload = linkedGroupFilters.map(item => ({
                filterId: item.id,
                excluded: !!item.excluded,
            }));

            if (linkedPersonAssignments.length || linkedGroupFiltersPayload.length) {
                tasks.push(
                    createFormEmployeeAssignment({
                        formId,
                        personAssignments: linkedPersonAssignments,
                        groupFilters: linkedGroupFiltersPayload,
                        linkedStudent: true,
                    }).unwrap()
                );
            }
        }

        if (!tasks.length) {
            return;
        }

        await Promise.all(tasks);
    };

    const createFormAndOpenPostCreateSetup = async () => {
        if (isCreatingForm) {
            return;
        }

        setIsCreatingForm(true);

        try {
            const payload = buildFormPayload();
            let formId = createdFormId;

            if (formId) {
                await updateForm({
                    ...payload,
                    id: formId,
                }).unwrap();
            } else {
                const result = await createForm(payload).unwrap();
                formId = result?.formId || result?.id;

                await saveRecipients(formId);

                if (
                    wizard.trigger?.selectedForm &&
                    wizard.trigger?.relatedComponent &&
                    wizard.trigger?.targetState
                ) {
                    await createFormQuestionAssignment({
                        formId,
                        relatedFormId: wizard.trigger.selectedForm.id,
                        relatedComponentId: wizard.trigger.relatedComponent.id,
                        targetState: `${wizard.trigger.targetState}`,
                        displayName: `${wizard.trigger.selectedForm.name} - ${wizard.trigger.relatedComponent.description}`,
                        ...(wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers &&
                            wizard.trigger.relatedFormEmailFieldId && {
                            relatedFormEmailFieldId: wizard.trigger.relatedFormEmailFieldId
                        }),
                        ...(wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
                            wizard.trigger.relatedFormEmployeeFieldId && {
                            relatedFormEmployeeFieldId: wizard.trigger.relatedFormEmployeeFieldId
                        })
                    }).unwrap();
                }
            }

            showSuccessNotification(
                formId === createdFormId
                    ? 'Form saved successfully'
                    : wizard.details.isCommunication
                        ? 'Email created successfully'
                        : 'Form created successfully'
            );
            setCreatedFormId(formId);
            setStage('postCreate');
        } catch (error) {
            showErrorNotification('Unable to create form');
        } finally {
            setIsCreatingForm(false);
        }
    };

    // The Access, Approvals and Automatic Responses chosen in the box, saved to the form once it exists.
    const saveBoxChoices = async (formId) => {
        const accessChanged = boxAccess.users.length > 0 ||
            boxAccess.roles.length !== 1 || boxAccess.roles[0] !== ROLES.SCHOOL_ADMIN ||
            (boxAccess.viewOnlyRoles ?? []).length > 0;
        if (accessChanged) {
            // The person creating the form always keeps access, as the server gives it to them.
            const current = await getFormAccess(formId).unwrap();
            const entry = Array.isArray(current) ? (current[0] || {}) : (current || {});
            const creatorIds = Array.isArray(entry.userIds) ? entry.userIds : (entry.users ?? []);
            await manageFormAccess({
                formId,
                userIds: Array.from(new Set([...creatorIds, ...boxAccess.users.map(user => user.id)])),
                roles: boxAccess.roles,
                viewOnlyUserIds: (boxAccess.viewOnlyUsers ?? []).filter(id => boxAccess.users.some(user => user.id === id)),
                viewOnlyRoles: (boxAccess.viewOnlyRoles ?? []).filter(role => boxAccess.roles.includes(role)),
            }).unwrap();
        }

        // Each stage is created after the stage it follows, so it can point at that stage's new id.
        const newIds = {};
        const remaining = [...boxStages];
        while (remaining.length) {
            const at = remaining.findIndex(stage => !stage.startsAfterStageId || newIds[stage.startsAfterStageId]);
            const [stage] = remaining.splice(at === -1 ? 0 : at, 1);
            const created = await createApprovalStage({
                formId,
                stage: {
                    name: stage.name,
                    employeeIds: stage.employeeIds ?? [],
                    employeeSchemaFieldId: stage.employeeSchemaFieldId ?? '',
                    startsAfterStageId: stage.startsAfterStageId ? (newIds[stage.startsAfterStageId] ?? null) : null,
                },
            }).unwrap();
            newIds[stage.id] = created?.id;
        }

        if (boxAutoresponderOn) {
            await setAutoresponderEnabled({formId, enabled: true}).unwrap();
        }
        for (const {id, ...rule} of boxRules) {
            await saveAutoresponderRule({formId, rule}).unwrap();
        }
    };

    // AD-2689: the form is created on the last page of the box, and opens in the form builder.
    const createFormInBox = async () => {
        if (!isSetupValid() || isCreatingForm || isTemplateSelectionLocked) {
            return null;
        }
        setIsCreatingForm(true);
        try {
            const result = await createForm(buildFormPayload()).unwrap();
            const formId = result?.formId || result?.id;

            await saveRecipients(formId);

            if (wizard.trigger?.selectedForm && wizard.trigger?.relatedComponent && wizard.trigger?.targetState) {
                await createFormQuestionAssignment({
                    formId,
                    relatedFormId: wizard.trigger.selectedForm.id,
                    relatedComponentId: wizard.trigger.relatedComponent.id,
                    targetState: `${wizard.trigger.targetState}`,
                    displayName: `${wizard.trigger.selectedForm.name} - ${wizard.trigger.relatedComponent.description}`,
                    ...(wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers &&
                        wizard.trigger.relatedFormEmailFieldId && {
                        relatedFormEmailFieldId: wizard.trigger.relatedFormEmailFieldId
                    }),
                    ...(wizard.form.audience === FORM_AUDIENCE_TYPE.employees &&
                        wizard.trigger.relatedFormEmployeeFieldId && {
                        relatedFormEmployeeFieldId: wizard.trigger.relatedFormEmployeeFieldId
                    })
                }).unwrap();
            }

            try {
                await saveBoxChoices(formId);
            } catch (error) {
                showErrorNotification('The form was created, but its access, approvals or automatic responses could not all be saved. Please check them in Form Settings.');
            }

            showSuccessNotification(wizard.details.isCommunication ? 'Email created successfully' : 'Form created successfully');
            navigate(`/forms/edit/${formId}`, {state: {fromTabIndex: 2}});
            return formId;
        } catch (error) {
            showErrorNotification('Unable to create form');
            return null;
        } finally {
            setIsCreatingForm(false);
        }
    };

    const isBuilderValid =
        Array.isArray(wizard.schema) &&
        wizard.schema.length > 0 &&
        Object.values(schemaValidation).filter(Boolean).length === 0;
    const builderPrimaryButtonLabel = isCreatingForm
        ? 'Saving...'
        : createdFormId
            ? 'Save & Continue'
            : (wizard.details.isCommunication ? 'Create email' : 'Create form');

    useEffect(() => {
        if (stage !== 'builder') {
            return;
        }

        setIsBuilderAtBottom(false);

        const scrollToTop = () => {
            const container = builderScrollContainerRef.current;

            if (container) {
                container.scrollTo({top: 0, left: 0, behavior: 'auto'});
            }

            window.scrollTo({top: 0, left: 0, behavior: 'auto'});
        };

        requestAnimationFrame(scrollToTop);
    }, [stage]);

    useEffect(() => {
        if (stage !== 'builder') {
            return undefined;
        }

        const sentinel = builderBottomSentinelRef.current;

        if (!sentinel) {
            return undefined;
        }

        const container = builderScrollContainerRef.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsBuilderAtBottom(entry.isIntersecting);
            },
            {
                root:
                    container && (container.scrollHeight - container.clientHeight) > 1
                        ? container
                        : null,
                rootMargin: '0px 0px 180px 0px',
                threshold: 0.01,
            }
        );

        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [stage, wizard.schema]);

    useEffect(() => {
        if (!isTemplateSelectionLocked) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setIsTemplateSelectionLocked(false);
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [isTemplateSelectionLocked]);

    const renderFloatingBuilderActionButtons = () => (
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={2}
            justifyContent={createdFormId ? 'flex-end' : 'space-between'}
            alignItems={{xs: 'stretch', sm: 'center'}}
            sx={{width: '100%'}}
        >
            {!createdFormId && (
                <Button
                    data-testid="wizard-back"
                    variant="outlined"
                    onClick={() => setStage('setup')}
                    sx={{
                        width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                        ml: {sm: 2},
                        mr: 'auto',
                    }}
                >
                    Back
                </Button>
            )}

            <Button
                data-testid="wizard-create-form"
                variant="contained"
                onClick={createFormAndOpenPostCreateSetup}
                disabled={!isBuilderValid || isCreatingForm}
                sx={{
                    width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                    px: 1,
                    ml: 'auto',
                    backgroundColor: '#186BBE',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#155FA7',
                        boxShadow: 'none'
                    }
                }}
            >
                {builderPrimaryButtonLabel}
            </Button>
        </Stack>
    );

    const renderAttachedBuilderActionButtons = () => (
        <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={2}
            justifyContent={createdFormId ? 'flex-end' : 'space-between'}
            alignItems={{xs: 'stretch', sm: 'center'}}
            sx={{width: '100%'}}
        >
            {!createdFormId && (
                <Button
                    data-testid="wizard-back"
                    variant="outlined"
                    onClick={() => setStage('setup')}
                    sx={{
                        width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                        ml: {sm: 2},
                        mr: 'auto',
                    }}
                >
                    Back
                </Button>
            )}

            <Button
                data-testid="wizard-create-form"
                variant="contained"
                onClick={createFormAndOpenPostCreateSetup}
                disabled={!isBuilderValid || isCreatingForm}
                sx={{
                    width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                    px: 1,
                    ml: 'auto',
                    backgroundColor: '#186BBE',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#155FA7',
                        boxShadow: 'none'
                    }
                }}
            >
                {builderPrimaryButtonLabel}
            </Button>
        </Stack>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 0,
                backgroundColor: '#fff',
            }}
        >
            {stage === 'setup'
                ? (
                    <Dialog
                        open
                        fullWidth
                        maxWidth="sm"
                        onClose={() => onClose()}
                        data-testid="create-form-dialog"
                        PaperProps={{sx: {borderRadius: 3, height: 'calc(100% - 64px)', ...(isWideBuildPage ? {maxWidth: 760} : {})}}}
                    >
                        <Box sx={{px: 4, pt: 2, pb: 1.5, borderBottom: '1px solid #eef0f3'}}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="overline" sx={{color: '#0DAD95', fontWeight: 700, letterSpacing: 1}}>
                                    {isSettingsStep ? 'Form Settings' : 'Form Setup'}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <IconButton size="small" onClick={() => onClose()} aria-label="Close">
                                        <CancelIcon fontSize="small"/>
                                    </IconButton>
                                </Stack>
                            </Stack>
                            <Typography variant="h5" sx={{fontWeight: 600}} data-testid="create-form-question">
                                {setupQuestion}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}} data-testid="create-form-line">
                                {setupLine}
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                                {setupSteps.map((step, index) => (
                                    <Box
                                        key={step}
                                        sx={{
                                            flex: 1,
                                            height: 4,
                                            borderRadius: 2,
                                            backgroundColor: index < setupStepNumber ? '#186BBE' : '#e3e8ef'
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        <DialogContent ref={setupContentRef} sx={{px: ['build', 'recipients', 'restrictions'].includes(setupStep) ? 3 : 4, pt: setupStep === 'recipients' ? 0.5 : (['restrictions', 'notifications', 'access', 'approvals', 'autoresponses', 'sharing'].includes(setupStep) ? 2 : (setupStep === 'build' ? 1.5 : 3)), pb: ['recipients', 'restrictions'].includes(setupStep) ? 1 : (setupStep === 'build' ? 1.5 : 3)}}>
                            {setupStep === 'type' && (
                                <RadioGroup
                                    data-testid="wizard-form-type"
                                    value={selectedFormType}
                                    onChange={handleFormTypeChange}
                                    sx={{gap: 0.5}}
                                >
                                    {CREATE_FORM_TYPE_OPTIONS.map(option => (
                                        <FormControlLabel
                                            key={option.id}
                                            value={option.id}
                                            control={<Radio size="small" sx={{py: 0.5}}/>}
                                            sx={{alignItems: 'flex-start', m: 0, minHeight: 32, '& .MuiFormControlLabel-label': {pt: '5px'}}}
                                            label={<RadioChoiceLabel text={option.description} tip={CHOICE_TIPS[option.id]}/>}
                                            data-testid={toTestId('option', option.description)}
                                        />
                                    ))}
                                </RadioGroup>
                            )}

                            {setupStep === 'audience' && (
                                <RadioGroup
                                    data-testid="wizard-audience"
                                    value={audienceGroup}
                                    onChange={handleAudienceGroupChange}
                                    sx={{gap: 0.5}}
                                >
                                    {audienceGroupOptions.map(option => (
                                        <FormControlLabel
                                            key={option.value}
                                            value={option.value}
                                            control={<Radio size="small" sx={{py: 0.5}}/>}
                                            sx={{alignItems: 'flex-start', m: 0, minHeight: 32, '& .MuiFormControlLabel-label': {pt: '5px'}}}
                                            label={<RadioChoiceLabel text={option.label} tip={CHOICE_TIPS[option.value]}/>}
                                            data-testid={toTestId('option', option.label)}
                                        />
                                    ))}
                                </RadioGroup>
                            )}

                            {setupStep === 'audienceDetail' && (
                                <Stack spacing={1.5}>
                                <RadioGroup
                                    data-testid="wizard-audience-detail"
                                    value={audienceDetailValue}
                                    onChange={handleAudienceDetailChange}
                                    sx={{gap: 0.5}}
                                >
                                    {(audienceDetailOptions[audienceGroup] || []).map(option => (
                                        <FormControlLabel
                                            key={option.value}
                                            value={option.value}
                                            control={<Radio size="small" sx={{py: 0.5}}/>}
                                            sx={{alignItems: 'flex-start', m: 0, minHeight: 32, '& .MuiFormControlLabel-label': {pt: '5px'}}}
                                            label={<RadioChoiceLabel text={option.label} helper={option.helper} tip={CHOICE_TIPS[option.value]}/>}
                                            data-testid={toTestId('option', option.label)}
                                        />
                                    ))}
                                </RadioGroup>
                                </Stack>
                            )}

                            {setupStep === 'build' && (
                                <Stack spacing={1} data-testid="wizard-build-method">
                                    {/* AD-2689: the three choices sit side by side, so the template list below them is in view. */}
                                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5}>
                                    {buildMethodChoices.map(choice => (
                                        <Box
                                            key={choice.value}
                                            role="button"
                                            tabIndex={0}
                                            data-testid={choice.testId}
                                            onClick={() => handleBuildMethodChange({target: {value: choice.value}})}
                                            onKeyDown={(event) => event.key === 'Enter' && handleBuildMethodChange({target: {value: choice.value}})}
                                            sx={{
                                                flex: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                textAlign: 'center',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                border: wizard.template.creationMethod === choice.value ? '2px solid #186BBE' : '1px solid #d9dee5',
                                                backgroundColor: wizard.template.creationMethod === choice.value ? '#f2f7fd' : '#fff',
                                                '&:hover': {borderColor: '#186BBE'},
                                            }}
                                        >
                                            <Typography fontWeight={600}>{choice.label}</Typography>
                                        </Box>
                                    ))}
                                    </Stack>
                                    {isTemplateMode && (isTemplateListShown || !selectedTemplateLabel) && (
                                        <StepTemplateSelection
                                            wizard={wizard}
                                            setWizard={setWizard}
                                            templateSource={templateSource}
                                            height="max(260px, calc(100vh - 360px))"
                                            onBack={() => setIsTemplateListShown(false)}
                                            onSelected={() => {
                                                setIsTemplateListShown(false);
                                                setIsAdvanceAfterTemplate(true);
                                            }}
                                        />
                                    )}
                                    {!!wizard.template.creationMethod && !(isTemplateMode && (isTemplateListShown || !selectedTemplateLabel)) && (
                                        <Stack direction="row" alignItems="center" spacing={2} sx={{pt: 1}}>
                                        {isTemplateMode && (
                                            <Button
                                                data-testid="wizard-choose-template"
                                                variant={selectedTemplateLabel ? 'outlined' : 'contained'}
                                                onClick={() => setIsTemplateListShown(true)}
                                                sx={{
                                                    boxShadow: 'none',
                                                    backgroundColor: selectedTemplateLabel ? undefined : '#186BBE',
                                                    '&:hover': {
                                                        boxShadow: 'none',
                                                        backgroundColor: selectedTemplateLabel ? undefined : '#155FA7',
                                                    }
                                                }}
                                            >
                                                {selectedTemplateLabel ? 'Change template' : 'Choose template'}
                                            </Button>
                                        )}
                                            {/* A blank form shows no name: its template is called 'Blank Form - Shareable' and so on. */}
                                            {selectedTemplateLabel && wizard.template.creationMethod !== 'blank' && (
                                                <Typography variant="body2" color="text.secondary" data-testid="create-form-chosen-template">
                                                    {selectedTemplateLabel}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                            )}

                            {isSettingsStep && (
                                <Stack spacing={3}>
                    {setupStep === 'recipients' && isTypeSelected && hasAudienceSelection && isTriggerForm && (
                        <ProgressiveSection>
                            <StepTriggerConditions
                                wizard={wizard}
                                setWizard={setWizard}
                            />
                        </ProgressiveSection>
                    )}

                    {setupStep === 'recipients' && isTypeSelected && hasAudienceSelection && !isTriggerForm && showRecipientSection && (
                        <ProgressiveSection>
                            <StepRecipientAssociation
                                wizard={wizard}
                                setWizard={setWizard}
                                wizardAccessLevel={accessLevel}
                                wizardAsksExternalLink
                            />
                        </ProgressiveSection>
                    )}

                    {setupStep === 'recipients' && isTypeSelected && hasAudienceSelection && !isTriggerForm && showExternalRecipients && (
                        <ProgressiveSection
                            header="External User Recipients"
                            headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                        >
                            <StepExternalRecipients
                                wizard={wizard}
                                setWizard={setWizard}
                            />
                        </ProgressiveSection>
                    )}

                    {setupStep === 'recipients' && isTypeSelected && hasAudienceSelection && !isTriggerForm && showLinkedStudents && (
                        <ProgressiveSection
                            header="Linked Students"
                            headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                        >
                            <StepEmployeeStudentAssociation
                                wizard={wizard}
                                setWizard={setWizard}
                                wizardAccessLevel={accessLevel}
                            />
                        </ProgressiveSection>
                    )}

                    {setupStep === 'recipients' && isTypeSelected && hasAudienceSelection && !isTriggerForm && showLinkedEmployees && (
                        <ProgressiveSection
                            header="Linked Employees"
                            headerIcon={<GroupsOutlinedIcon fontSize="small"/>}
                        >
                            <StepEmployeeEmployeeAssociation
                                wizard={wizard}
                                setWizard={setWizard}
                                wizardAccessLevel={accessLevel}
                            />
                        </ProgressiveSection>
                    )}

                    {setupStep === 'restrictions' && (
                        <Box data-testid="create-form-restrictions">
                            <RestrictionQuestions items={restrictions}/>
                        </Box>
                    )}

                    {setupStep === 'name' && canShowFormDetailsSection && (
                        <ProgressiveSection>
                            <Stack spacing={1.5}>
                                <Typography variant="body2" data-testid="wizard-name-line">
                                    {isEmailBox
                                        ? 'Choose a name for your email.'
                                        : 'Choose a name for your form. If you have used a template, the template name will be the default form name, but you can change this if you want to.'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    // AD-2689: the name is shown inside the box until it is filled in, never in its border.
                                    placeholder={wizard.details.isCommunication ? 'Email Name' : 'Form Name'}
                                    data-testid="wizard-form-name"
                                    autoComplete="off"
                                    required
                                    value={wizard.details.formName}
                                    // The name is only marked as missing after the box has been used, not when the page opens.
                                    error={isNameTouched && !formNameValidation.isValid}
                                    helperText={(isNameTouched && formNameValidation.message) || ' '}
                                    inputProps={{maxLength: FORM_NAME_MAX_LENGTH, 'aria-label': wizard.details.isCommunication ? 'Email Name' : 'Form Name'}}
                                    onBlur={() => setIsNameTouched(true)}
                                    onChange={(event) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            details: {
                                                ...prev.details,
                                                formName: event.target.value,
                                            }
                                        }))
                                    }
                                />

                                <Typography variant="body2" data-testid="wizard-category-line">
                                    Selecting a category for your {isEmailBox ? 'email' : 'form'} makes it easy to sort and filter the lists on the Forms page. Categories are created in School Settings.
                                </Typography>
                                <TextField
                                    select
                                    fullWidth
                                    data-testid="wizard-categories"
                                    value={wizard.details.categories}
                                    label="Select a category"
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => {
                                            if (!formCategories?.length) {
                                                return <em>No categories</em>;
                                            }

                                            if (!Array.isArray(selected) || selected.length === 0) {
                                                return <em>No categories</em>;
                                            }

                                            const selectedCats = formCategories.filter(category =>
                                                selected.includes(category.id)
                                            );

                                            return (
                                                <Stack flexDirection="row" gap={1} flexWrap="wrap">
                                                    {selectedCats.map(category => (
                                                        <Box
                                                            key={category.id}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.8,
                                                                padding: 1,
                                                                borderRadius: '18px',
                                                                border: '1px solid #adadad',
                                                            }}
                                                        >
                                                            <CircleIcon
                                                                fontSize="small"
                                                                sx={{color: category.color}}
                                                            />
                                                            <Typography variant="body2">
                                                                {category.name}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            );
                                        },
                                        MenuProps: {
                                            sx: {
                                                '& .MuiMenuItem-root': {
                                                    whiteSpace: 'unset',
                                                    wordBreak: 'break-all'
                                                }
                                            }
                                        }
                                    }}
                                    onChange={(event) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            details: {
                                                ...prev.details,
                                                categories: event.target.value,
                                            }
                                        }))
                                    }
                                >
                                    {!formCategories?.length && (
                                        <MenuItem disabled>
                                            <em>No categories</em>
                                        </MenuItem>
                                    )}
                                    {formCategories.map(category => (
                                        <MenuItem key={category.id} value={category.id}>
                                            <Checkbox checked={wizard.details.categories.includes(category.id)}/>
                                            <CircleIcon
                                                fontSize="small"
                                                sx={{
                                                    color: category.color,
                                                    mr: 1
                                                }}
                                            />
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </ProgressiveSection>
                    )}

                                {['expiry', 'notifications'].includes(setupStep) && canShowSettingsSection && (
                                    <ProgressiveSection>
                                        <Stack spacing={1.5}>
                                            {setupStep === 'expiry' && shouldShowExpirationDate && (
                                                <FormExpirationDatePicker
                                                    hideEmptyPrompt
                                                    label="Expiry date (optional)"
                                                    expirationDate={wizard.details.expiryDate || null}
                                                    onExpirationDateChange={(expiryDate) =>
                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                expiryDate,
                                                            },
                                                        }))
                                                    }
                                                />
                                            )}

                                            {setupStep === 'notifications' && shouldShowReminderField && (
                                                <Typography variant="body2" data-testid="wizard-reminder-line">
                                                    Admicity will send automated reminders to your form recipients. Please choose a reminder frequency in hours (for example, enter 72 if you want a reminder to be sent every three days). A maximum of three reminders will be sent. Leave this box empty if you don&apos;t want any reminders to be sent.
                                                </Typography>
                                            )}
                                            {setupStep === 'notifications' && shouldShowReminderField && (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Reminder Interval (optional)"
                                                    variant="outlined"
                                                    value={wizard.details.reminderFrequency || ''}
                                                    onChange={(event) => {
                                                        const nextValue = parseRemindIntervalInput(event.target.value);
                                                        if (nextValue === null) {
                                                            return;
                                                        }
                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                reminderFrequency: nextValue,
                                                            }
                                                        }));
                                                    }}
                                                    inputProps={{
                                                        inputMode: 'numeric',
                                                        pattern: '[0-9]*'
                                                    }}
                                                />
                                            )}

                                            {setupStep === 'notifications' && shouldShowNotificationEmails && (
                                                <Typography variant="body2" data-testid="wizard-notification-line">
                                                    You can easily check your form responses in Admicity, but if you want an email notification when a submission is made for your form, enter the email address here. You can enter up to five email addresses, pressing Enter after each one.
                                                </Typography>
                                            )}
                                            {setupStep === 'notifications' && shouldShowNotificationEmails && (
                                                <EmailsInput
                                                    hideHelperText
                                                    size="small"
                                                    label="Enter your email address(es) here to receive notifications"
                                                    values={wizard.details.notificationEmail.map(email => ({email}))}
                                                    onChange={(emails) =>
                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                notificationEmail: Array.from(
                                                                    new Map(
                                                                        (emails || []).map(item => [item.email, item.email])
                                                                    ).values()
                                                                )
                                                            }
                                                        }))
                                                    }
                                                    onBlur={(email) => {
                                                        if (!email) {
                                                            return;
                                                        }

                                                        setWizard(prev => ({
                                                            ...prev,
                                                            details: {
                                                                ...prev.details,
                                                                notificationEmail: Array.from(
                                                                    new Map([
                                                                        ...prev.details.notificationEmail.map(item => [item, item]),
                                                                        [email, email]
                                                                    ]).values()
                                                                )
                                                            }
                                                        }));
                                                    }}
                                                />
                                            )}
                                        </Stack>
                                    </ProgressiveSection>
                                )}
                                </Stack>
                            )}
                            {setupStep === 'sharing' && (
                                <Typography variant="body2" color="text.secondary" data-testid="wizard-sharing">
                                    The Settings tab also has the embed code, if you want the form to appear inside a
                                    page of your school website.
                                </Typography>
                            )}
                            {setupStep === 'access' && (
                                <FormAccessSettings
                                    formName={wizard.details.formName}
                                    wizardMode
                                    pending={{...boxAccess, onChange: setBoxAccess}}
                                />
                            )}
                            {setupStep === 'approvals' && (
                                <ApprovalSettings
                                    pendingStages={boxStages}
                                    onPendingStagesChange={setBoxStages}
                                    schema={wizard.schema}
                                    isFormLive={false}
                                    isFormPrivate={accessLevel === FORM_ACCESS_LEVELS.private}
                                    isFormArchived={false}
                                />
                            )}
                            {setupStep === 'autoresponses' && (
                                <AutoresponderSettings
                                    wizardMode
                                    pendingRules={boxRules}
                                    onPendingRulesChange={setBoxRules}
                                    schema={wizard.schema}
                                    enableAutoresponder={boxAutoresponderOn}
                                    onAutoresponderToggle={setBoxAutoresponderOn}
                                    accessLevel={accessLevel}
                                    isFormArchived={false}
                                    audience={wizard.form.audience}
                                    recipientType={wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers
                                        ? (wizard.details.linkToEmployees ? 'employee' : 'student')
                                        : null}
                                    linkedToStudents={!!wizard.details.linkToStudents}
                                    linkedToEmployees={!!wizard.details.linkToEmployees}
                                />
                            )}
                        </DialogContent>

                        <Box sx={{px: 4, py: 2, borderTop: '1px solid #eef0f3'}}>
                            <Stack direction="row" justifyContent="space-between">
                                <Button
                                    data-testid="wizard-back"
                                    variant="outlined"
                                    onClick={() => setupStepNumber === 1 ? onClose() : setSetupStep(setupSteps[setupStepNumber - 2])}
                                >
                                    Back
                                </Button>
                                {setupStep === 'recipients' && !recipientGroupsCheck.isValid && (
                                    <Typography
                                        variant="body2"
                                        color="error"
                                        data-testid="wizard-recipients-error"
                                        sx={{alignSelf: 'center', px: 2}}
                                    >
                                        {recipientGroupsCheck.error}
                                    </Typography>
                                )}
                                {isLastSetupStep
                                    ? (
                                        <Button
                                            data-testid="wizard-continue"
                                            variant="contained"
                                            onClick={createFormInBox}
                                            disabled={!isSetupValid() || isCreatingForm || isTemplateSelectionLocked}
                                            sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                                        >
                                            {isCreatingForm ? 'Creating...' : (isEmailBox ? 'Create email' : 'Create form')}
                                        </Button>
                                    )
                                    : (
                                        <Button
                                            data-testid="wizard-next"
                                            variant="contained"
                                            onClick={goToNextSetupStep}
                                            disabled={!canGoNext}
                                            sx={{backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}}}
                                        >
                                            Next
                                        </Button>
                                    )}
                            </Stack>
                        </Box>
                    </Dialog>
                )
                : stage === 'builder'
                    ? (
                        <>
                            <Box sx={{flex: 1, minHeight: 0}}>
                                <StepFormEditor
                                    wizard={wizard}
                                    setWizard={setWizard}
                                    formId={createdFormId}
                                    accessLevel={accessLevel}
                                    schema={wizard.schema}
                                    onSchemaChange={(nextSchema) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            schema: nextSchema,
                                        }))
                                    }
                                    componentToEditIndex={componentToEditIndex}
                                    setComponentToEditIndex={setComponentToEditIndex}
                                    onSchemaValidationChange={setSchemaValidation}
                                    scrollContainerRef={builderScrollContainerRef}
                                    onScrollContainerScroll={undefined}
                                />
                            </Box>

                            <Box
                                ref={builderBottomSentinelRef}
                                sx={{
                                    height: 1,
                                    width: '100%',
                                }}
                            />

                            {isBuilderAtBottom && (
                                <Box
                                    sx={{
                                        borderTop: '1px solid #e0e0e0',
                                        backgroundColor: '#fff',
                                        px: 2,
                                        py: 2,
                                    }}
                                >
                                    {renderAttachedBuilderActionButtons()}
                                </Box>
                            )}
                        </>
                    )
                    : (
                        <FormPostCreateSetup
                            formIdOverride={createdFormId}
                            onBackToBuilder={() => setStage('builder')}
                        />
                )}

            {stage === 'builder' && !isBuilderAtBottom && !isSmallScreen && (
                <Box
                        sx={(theme) => ({
                            position: 'fixed',
                            bottom: theme.spacing(2),
                            left: theme.spacing(2),
                            // AD-2659: clear of Felicity's side panel while it is open, and of her button otherwise.
                            right: `calc(${theme.spacing(2)} + max(${FELICITY_FAB_CLEARANCE}px, var(--felicity-panel-width, 0px)))`,
                            zIndex: theme.zIndex.drawer + 4,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            pointerEvents: 'none',
                        })}
                    >
                    <Box
                        sx={{
                            width: '100%',
                            px: {xs: 1, lg: 1.5, xl: 2},
                            pointerEvents: 'auto',
                        }}
                    >
                        {renderFloatingBuilderActionButtons()}
                    </Box>
                </Box>
            )}

            <Dialog
                open={isTemplateDialogOpen}
                onClose={() => setIsTemplateDialogOpen(false)}
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle sx={{pr: 6}}>
                    Choose a Template
                    <IconButton
                        onClick={() => setIsTemplateDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: 12,
                        }}
                    >
                        <CancelIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{p: 0}}>
                    <StepTemplateSelection
                        wizard={wizard}
                        setWizard={setWizard}
                        templateSource={templateSource}
                        onBack={() => setIsTemplateDialogOpen(false)}
                        onSelected={() => {
                            setIsTemplateSelectionLocked(true);
                            setIsTemplateDialogOpen(false);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default CreateFormWizard;
